import type { ReactNode } from "react";

import { dispatchConsolePages, resolvePage } from "../navigation.ts";
import type { Session } from "../session.ts";

/**
 * What this console can and cannot do yet, said out loud.
 *
 * Not a placeholder. Building the console against the real contracts turned up
 * a gap larger than any screen: **no service exposes a list**. Of 170 GET
 * operations across 141 services, 21 return a collection and almost all of
 * those are the children of one parent — a customer's consents, a driver's
 * documents, a shipment's tracking timeline. There is no "trips in flight", no
 * "dispatches waiting", no "open exceptions", no "drivers online".
 *
 * Every page of a dispatch console is a list, so none of them can be built
 * against the services directly. And they were never meant to be: Document 04
 * puts a BFF between each channel and the domain — "BFFs may aggregate and
 * transform responses, compose read models and optimize network round trips" —
 * and Document 05 catalogues six of them, EXP-002 through EXP-007, with
 * purposes, dependencies and security tiers. None is implemented. There is no
 * experience tier at all.
 *
 * So this page is what the console honestly is today: a working shell, a
 * permission model that decides what an operator may reach, and an account of
 * what each page is waiting for. An operator opening it learns something true.
 * A stub pretending to be a demand queue would not.
 */

export interface PageOutlookProps {
  readonly session: Session;
}

export function PageOutlook({ session }: PageOutlookProps): ReactNode {
  const rows = dispatchConsolePages.map((page) => ({
    page,
    resolved: resolvePage(page, session.scopes),
  }));

  return (
    <section className="page">
      <h1 className="page__title">Console readiness</h1>
      <p className="page__lead">
        This console can work out what you are allowed to see, but it cannot show it to you yet. The
        service that assembles these views for the operations channel has not been built, so every
        page below is waiting on the same thing.
      </p>

      <table className="outlook">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col">Your access</th>
            <th scope="col">Waiting on</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ page, resolved }) => (
            <tr key={page.id}>
              <th scope="row">{page.title}</th>
              <td>
                {resolved.access === "forbidden"
                  ? page.blockedBy === undefined
                    ? "No permission"
                    : "Not available"
                  : resolved.access === "read_only"
                    ? "View only"
                    : "Full"}
              </td>
              <td>
                {page.blockedBy ?? "A way to list these. Your permissions are already in place."}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
